/**
 * @fullvsl/contracts/overlay-templates — Default MVP overlay templates (FUL-10).
 *
 * Six editable overlay templates for the MVP moments: start, smart autoplay,
 * in-video mini-hook, pause, CTA reveal, and end. Each template is a partial
 * `Overlay` (FUL-3 §3) materialized with sensible defaults; marketers clone and
 * override only the fields that differ.
 *
 * Product spec: docs/fullvsl-overlay-templates.md. Design tokens: FullVSL design
 * system (primary #00685f, Inter typography, 4px/8px radii). Safe-zone positions
 * (FUL-8): top/center/bottom + the four corners.
 *
 * Security: assetType "html" is accepted by the contract but the MVP renderer
 * treats it as disabled (text-only fallback) — no user/remote HTML is ever
 * inserted into the DOM (PRD §9.7, US-5.3 #2).
 */

import type { Overlay, OverlayContent, OverlayType } from "./contracts";

/** Default appearance inherited by every template field left blank. */
export const OVERLAY_DESIGN_TOKENS = {
  font: "Inter, system-ui, -apple-system, sans-serif",
  primary: "#00685f",
  onPrimary: "#ffffff",
  primaryContainer: "#008378",
  scrimBackground: "rgba(0,0,0,0.55)",
  scrimText: "#ffffff",
  radiusButton: "4px",
  radiusPanel: "8px",
} as const;

/** Scrim colors applied to the default `content.colors`. */
const SCRIM_COLORS: OverlayContent["colors"] = {
  background: OVERLAY_DESIGN_TOKENS.scrimBackground,
  text: OVERLAY_DESIGN_TOKENS.scrimText,
  button: OVERLAY_DESIGN_TOKENS.primary,
  buttonText: OVERLAY_DESIGN_TOKENS.onPrimary,
};

/**
 * Minimum watched seconds before the pause overlay may appear. Resolves PRD §18
 * ("should pause overlay appear on every pause or only after a threshold?") to a
 * threshold default, so accidental/buffering micro-pauses don't harass the viewer.
 */
export const PAUSE_OVERLAY_MIN_WATCH_SEC = 10;

/**
 * A default template is everything except the runtime `id` (assigned on
 * instantiation) and the per-VSL timing overrides a marketer sets. Field typing
 * mirrors `Overlay` so cloning + an `id` yields a valid Overlay.
 */
export type OverlayTemplate = Omit<Overlay, "id">;

/** Start overlay — pre-first-play invitation when autoplay is off/blocked. */
export const START_TEMPLATE: OverlayTemplate = {
  type: "start",
  timing: { show: "immediate" },
  content: {
    text: "Assista à apresentação gratuita",
    buttonLabel: "▶ Reproduzir",
    buttonAction: "continue",
    position: "center",
    animation: "fade",
    bgOpacity: 0.55,
    font: OVERLAY_DESIGN_TOKENS.font,
    colors: SCRIM_COLORS,
  },
  actions: ["continue"],
};

/** Smart-autoplay overlay — asks the viewer to tap/click for sound. */
export const AUTOPLAY_TEMPLATE: OverlayTemplate = {
  type: "autoplay",
  timing: { show: "immediate" },
  content: {
    text: "Toque para ativar o som",
    subtext: "Reprodução automática sem som",
    buttonLabel: "🔊 Ativar som",
    buttonAction: "continue",
    position: "bottom",
    animation: "fade",
    bgOpacity: 0.55,
    font: OVERLAY_DESIGN_TOKENS.font,
    colors: SCRIM_COLORS,
  },
  actions: ["continue"],
};

/**
 * In-video mini-hook overlay — optional low-profile nudge at a configured
 * timestamp. `timing.at` has no safe default and MUST be set by the marketer;
 * the editor should keep this template disabled until then.
 */
export const MINIHOOK_TEMPLATE: OverlayTemplate = {
  type: "minihook",
  timing: { show: "timestamp", at: 0, durationMs: 5000 },
  content: {
    position: "top_right",
    animation: "slide",
    bgOpacity: 0.5,
    font: OVERLAY_DESIGN_TOKENS.font,
    colors: SCRIM_COLORS,
  },
  actions: ["close"],
};

/** Pause overlay — recovery actions instead of a dead end (≥ min-watch). */
export const PAUSE_TEMPLATE: OverlayTemplate = {
  type: "pause",
  timing: { show: "onPause" },
  content: {
    text: "Espere! Não perca isto.",
    subtext: "Continue assistindo ou vá direto para a oferta.",
    buttonLabel: "▶ Continuar",
    buttonAction: "continue",
    position: "center",
    animation: "fade",
    bgOpacity: 0.6,
    font: OVERLAY_DESIGN_TOKENS.font,
    colors: SCRIM_COLORS,
  },
  actions: ["continue", "restart", "jump_to_offer", "open_cta", "close"],
};

/** CTA-reveal overlay — styles the delayed CTA (FUL-3 §2) inside the frame. */
export const CTA_REVEAL_TEMPLATE: OverlayTemplate = {
  type: "cta_reveal",
  timing: { show: "timestamp", at: 0 },
  content: {
    text: "Garanta sua vaga",
    buttonLabel: "Acessar agora →",
    buttonAction: "open_cta",
    position: "bottom",
    animation: "slide",
    bgOpacity: 0.65,
    font: OVERLAY_DESIGN_TOKENS.font,
    colors: SCRIM_COLORS,
  },
  actions: ["open_cta", "close"],
};

/**
 * End overlay — shown on completion, drives the final conversion. The contract
 * `OverlayShowMoment` has no literal for completion; the player binds this
 * template to the `completed` event (open item #1 in the product spec).
 */
export const END_TEMPLATE: OverlayTemplate = {
  type: "end",
  timing: { show: "onReplay" },
  content: {
    text: "Tudo pronto!",
    subtext: "Pronto para dar o próximo passo?",
    buttonLabel: "Acessar agora →",
    buttonAction: "open_cta",
    position: "center",
    animation: "fade",
    bgOpacity: 0.7,
    font: OVERLAY_DESIGN_TOKENS.font,
    colors: SCRIM_COLORS,
  },
  actions: ["open_cta", "restart", "close"],
};

/** Ordered default library keyed by overlay type. */
export const OVERLAY_TEMPLATES: Record<OverlayType, OverlayTemplate> = {
  start: START_TEMPLATE,
  autoplay: AUTOPLAY_TEMPLATE,
  minihook: MINIHOOK_TEMPLATE,
  pause: PAUSE_TEMPLATE,
  cta_reveal: CTA_REVEAL_TEMPLATE,
  end: END_TEMPLATE,
};

/**
 * Instantiate a template as a concrete `Overlay`. `idFactory` defaults to a
 * crypto-free generator suitable for the MVP; callers may inject a uuid v4
 * generator for server-side persistence.
 */
export function instantiateOverlay(
  type: OverlayType,
  overrides: Partial<OverlayTemplate> = {},
  idFactory: () => string = defaultOverlayId,
): Overlay {
  const base = OVERLAY_TEMPLATES[type];
  return {
    id: idFactory(),
    ...base,
    ...overrides,
    timing: { ...base.timing, ...overrides.timing },
    content: {
      ...base.content,
      ...overrides.content,
      colors: {
        ...base.content.colors,
        ...overrides.content?.colors,
      },
    },
    actions: [...(overrides.actions ?? base.actions)],
  } as Overlay;
}

/**
 * Create a fresh, independent overlay set for one VSL. Presets are copied on
 * creation; changing one VSL must never mutate another VSL or the preset library.
 */
export function createDefaultOverlays(): Overlay[] {
  return [
    instantiateOverlay("start"),
    instantiateOverlay("autoplay"),
    instantiateOverlay("pause"),
    instantiateOverlay("cta_reveal", { timing: { show: "timestamp", at: 0 } }),
    instantiateOverlay("end"),
  ];
}

/** Deep-copy a persisted overlay before exposing it to an editor. */
export function cloneOverlay(overlay: Overlay): Overlay {
  return {
    ...overlay,
    timing: { ...overlay.timing },
    content: {
      ...overlay.content,
      colors: overlay.content.colors ? { ...overlay.content.colors } : undefined,
    },
    actions: [...overlay.actions],
  };
}

/** MVP id generator (crypto-free, matches player prototype conventions). */
export function defaultOverlayId(): string {
  const rnd = Math.random().toString(36).slice(2, 10);
  const time = Date.now().toString(36).slice(-4);
  return `overlay_${time}${rnd}`;
}
